import moment from "moment";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { auth } from "services/firebase.config";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { errorToast } from "components/toast/toast";
import axios from "axios";

export const getCurrentYear = () => new Date().getFullYear();

const mapUploadHeaders = (headers = {}) => {
  const mappedHeaders = {};

  if (headers.contentType) {
    mappedHeaders["Content-Type"] = headers.contentType;
  }

  if (headers.cacheControl) {
    mappedHeaders["Cache-Control"] = headers.cacheControl;
  }

  return mappedHeaders;
};

export const uploadToSignedUrl = async (file, uploadConfig) => {
  if (!uploadConfig?.uploadUrl) return null;
  if (typeof file === "string") return file;

  try {
    const method = uploadConfig.method?.toUpperCase?.() || "PUT";
    const headers = mapUploadHeaders(uploadConfig.headers);

    const response = await axios({
      method,
      url: uploadConfig.uploadUrl,
      data: file,
      headers,
    });

    return (
      uploadConfig.mediaUrl ||
      response?.data?.mediaUrl ||
      response?.data?.url ||
      response?.data?.secure_url ||
      ""
    );
  } catch (error) {
    errorToast("Error", error);
    return null;
  }
};

export const getFileType2 = (fileUrl) => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
  const videoExtensions = ["mp4", "avi", "mov", "wmv"];
  const docExtensions = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"];

  const fileExtension = getFileExtension(fileUrl);

  if (imageExtensions.includes(fileExtension)) return "IMAGE";
  if (videoExtensions.includes(fileExtension)) return "VIDEO";
  if (docExtensions.includes(fileExtension)) return "DOC";
  return "other";
};

export const getFileType = (fileName) => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
  const videoExtensions = ["mp4", "avi", "mov", "wmv"];
  const pdfExtensions = ["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"];

  const fileExtension = getFileExtension(fileName);

  if (imageExtensions.includes(fileExtension)) return "image";
  if (videoExtensions.includes(fileExtension)) return "video";
  if (pdfExtensions.includes(fileExtension)) return "pdf";
  return "other";
};

export const getCTAItemsLabel = (type) => {
  switch (type) {
    case "EVENT":
      return "View Event";
    case "WEBINAR":
      return "View Webinar";
    case "TRIP":
      return "View Package";
    case "ARTICLES":
      return "Read Article";
    default:
      return "View Event";
  }
};

export const getMoreItemsLabel = (type) => {
  switch (type) {
    case "EVENT":
      return "More Events";
    case "WEBINAR":
      return "More Webinars";
    case "TRIP":
      return "More Trips";
    case "ARTICLES":
      return "More Articles";
    default:
      return "More Events";
  }
};

export const getSingularType = (type) => {
  switch (type) {
    case "EVENT":
      return "event";
    case "WEBINAR":
      return "webinar";
    case "TRIP":
      return "trip";
    case "ARTICLES":
      return "article";
    default:
      return "event";
  }
};

export const featuredCTA = (type) => {
  switch (type) {
    case "EVENT":
      return "View Event";
    case "WEBINAR":
      return "Join Webinar";
    case "TRIP":
      return "View Trip";
    case "ARTICLES":
      return "Read Article";
    default:
      return "View Event";
  }
};

export const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const formatTimestampForUser = (firebaseTimestamp) => {
  if (!firebaseTimestamp || !userTimeZone) return "";

  const date = new Date(firebaseTimestamp); // Convert to JS Date
  const zonedDate = toZonedTime(date, userTimeZone);
  return format(zonedDate, "hh:mma");
};

export const checkDeadlineRequirement = (departureDate, var_days) => {
  const today = new Date();

  // Convert the departure date string to a Date object
  const departureDateObj = new Date(departureDate);

  // Calculate difference in milliseconds
  const differenceInTime = departureDateObj.getTime() - today.getTime();

  // Convert to days
  const differenceInDays = Math.floor(differenceInTime / (1000 * 3600 * 24));

  // Check if the difference is greater than or equal to the deadline requirement
  return differenceInDays >= var_days;
};

export const subtractDaysAndKeepISOFormat = (isoDateStr, daysToSubtract) => {
  if (!isoDateStr) {
    return;
  }
  // Parse the ISO date string
  const date = new Date(isoDateStr);

  // Subtract the specified number of days
  date?.setDate(date.getDate() - daysToSubtract);

  // Return the result in ISO format
  return date.toISOString();
};
export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const accType = sessionStorage.getItem("account_type");

export const formatter = accType?.includes("NIGERIAN")
  ? new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    })
  : new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    });

export const debounce = (fn, delay) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const logoutUser = (navigate) => {
  sessionStorage.clear();
  auth.signOut();
  navigate("/");
  window.location.reload();
};

export const isInThePast = (date) => {
  const today = new Date();
  const newDate = new Date(date);

  // 👇️ OPTIONAL!
  // This line sets the hour of the current date to midnight
  // so the comparison only returns `true` if the passed in date
  // is at least yesterday
  today.setHours(0, 0, 0, 0);

  return newDate < today;
};

export const formatDate = (date, fallback = "") => {
  if (!date) return fallback;

  return new Date(date).toLocaleDateString("default", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
};

export const trim = (str) => {
  return str?.length > 45 ? str.substring(0, 45) + "..." : str;
};

export function closestNumber(r_days, freq) {
  let q = Math.floor(r_days / freq);

  let n1 = freq * q;

  let n2 = r_days * freq > 0 ? freq * (q + 1) : freq * (q - 1);

  if (Math.abs(r_days - n1) < Math.abs(r_days - n2)) return n1;

  return n2;
}

export function getNumberOfDays(targetDate) {
  const today = new Date().toJSON();

  const date1 = new Date(moment(today).format("l"));
  const date2 = new Date(moment(targetDate).format("l"));

  // One day in milliseconds
  const oneDay = 1000 * 60 * 60 * 24;

  // Calculating the time difference between two dates
  const diffInTime = date2.getTime() - date1.getTime();

  // Calculating the no. of days between two dates
  const diffInDays = Math.floor(diffInTime / oneDay);

  return diffInDays;
}

export function getOriginalFilename(cloudinaryLink) {
  const urlParts = cloudinaryLink.split("/");
  const filenameWithExtension = urlParts[urlParts.length - 1];
  const filename = filenameWithExtension.split(".")[0];
  return filename;
}

export function isDatePastToday(date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set hours, minutes, seconds, and milliseconds to 0 for accurate comparison

  const inputDate = new Date(date);
  inputDate.setHours(0, 0, 0, 0);

  return inputDate < today;
}

export const getFileExtension = (fileName) => {
  return fileName?.split(".")?.pop().toLowerCase();
};

export const downloadFile = async (fileUrl, fileName) => {
  try {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "download";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

export const viewFile = async (fileUrl) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.click();
};

export const extractFileName = (url) => {
  try {
    // Decode the URL to handle encoded characters
    const decodedUrl = decodeURIComponent(url);

    // Split the URL by '/' and get the last segment
    const urlParts = decodedUrl.split("/");
    const lastPart = urlParts[urlParts.length - 1];

    // Only remove Cloudinary identifiers (e.g., _rks69t) but keep the file extension
    // This regex removes only Cloudinary identifier patterns like _rks69t but preserves the extension
    const fileName = lastPart.replace(/(_[a-z0-9]+)(?=\.[a-zA-Z0-9]+$)/, "");

    // Replace underscores with spaces for readability (except in the extension)
    // Find the last period to identify where the extension starts
    const lastDotIndex = fileName.lastIndexOf(".");

    if (lastDotIndex === -1) {
      // No extension found, just replace all underscores
      return fileName.replace(/_/g, " ");
    } else {
      // Replace underscores only in the filename part, not in the extension
      const name = fileName.substring(0, lastDotIndex);
      const extension = fileName.substring(lastDotIndex);
      return name.replace(/_/g, " ") + extension;
    }
  } catch (error) {
    console.error("Error extracting filename:", error);
    return "Unknown Document";
  }
};

export const formatTimestamp = (timeInput) => {
  if (!timeInput) return ""; // Handle cases where timeInput might be undefined

  let timestamp;

  // Check if timeInput is a number (Unix seconds) or a string (ISO date)
  if (typeof timeInput === "number") {
    // Handle Unix timestamp in seconds
    timestamp = moment.unix(timeInput);
  } else if (typeof timeInput === "string") {
    // Handle ISO date string
    timestamp = moment(timeInput);
  } else if (timeInput instanceof Date) {
    // Handle Date object
    timestamp = moment(timeInput);
  } else {
    // If it's some other format we can't process
    return "";
  }

  const now = moment();

  if (timestamp.isSame(now, "day")) {
    // Same day: Show time in hh:mmA format
    return timestamp.format("hh:mmA");
  } else {
    // Different day: Show date in 'Do MMM' format (e.g., 24th Mar)
    return timestamp.format("Do MMM");
  }
};

export const formatFileSize = (bytes) => {
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;

  if (bytes < kb) return `${bytes} B`;
  if (bytes < mb) return `${(bytes / kb).toFixed(2)} KB`;
  if (bytes < gb) return `${(bytes / mb).toFixed(2)} MB`;
  return `${(bytes / gb).toFixed(2)} GB`;
};

export const findBestPackagePrice = (room_rates) => {
  if (!room_rates || room_rates.length === 0) return null;

  const validRates = room_rates.filter((rate) => {
    const r = parseFloat(rate.rate);
    return !isNaN(r) && r > 0;
  });

  if (validRates.length === 0) return null;

  const sortedRates = validRates.sort(
    (a, b) => parseFloat(a.rate) - parseFloat(b.rate),
  );

  return sortedRates[0].rate;
};

export const extractCustomTripId = (message, textString) => {
  if (!message || !message.startsWith(textString)) {
    return null;
  }

  const startIndex = message.indexOf("-") + 1;
  const endIndex = message.indexOf("*");

  if (startIndex > 0 && endIndex > startIndex) {
    return message.substring(startIndex, endIndex);
  }

  return null;
};
